/* Fixture catalog: plan-symbol drawing in inch space, origin at center,
   +y down, "back" faces −y (so rot 0 backs onto a wall above it). */

type Ctx = CanvasRenderingContext2D;

export function rr(ctx: Ctx, x: number, y: number, w: number, h: number, r: number): void {
  r = Math.min(r, w / 2, h / 2);
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export interface FixtureDef { w: number; h: number; label: string; draw: (c: Ctx, w: number, h: number) => void }

/* bed plan symbol: frame, pillow(s) at the head (-y), fold-back line */
function drawBed(c: Ctx, w: number, h: number): void {
  c.beginPath(); rr(c, -w / 2, -h / 2, w, h, 1.5); c.stroke();
  if (w > 45) {
    c.beginPath(); rr(c, -w / 2 + 4, -h / 2 + 3, w / 2 - 7, 10, 2.5); c.stroke();
    c.beginPath(); rr(c, 3, -h / 2 + 3, w / 2 - 7, 10, 2.5); c.stroke();
  } else {
    c.beginPath(); rr(c, -w / 2 + 5, -h / 2 + 3, w - 10, 10, 2.5); c.stroke();
  }
  c.beginPath(); c.moveTo(-w / 2, -h / 2 + 20); c.lineTo(w / 2, -h / 2 + 20); c.stroke();
  c.beginPath(); c.moveTo(w / 2 - 9, h / 2); c.lineTo(w / 2, h / 2 - 9); c.stroke();
}

/* sofa plan symbol: back at -y, arms both ends; seat split into `seats` cushions */
function drawSofa(c: Ctx, w: number, h: number, seats: number): void {
  c.beginPath(); rr(c, -w / 2, -h / 2, w, 7, 2.5); c.stroke();
  c.beginPath(); rr(c, -w / 2, -h / 2, 7, h, 2.5); c.stroke();
  c.beginPath(); rr(c, w / 2 - 7, -h / 2, 7, h, 2.5); c.stroke();
  c.beginPath(); rr(c, -w / 2 + 7, -h / 2 + 7, w - 14, h - 7, 1.5); c.stroke();
  c.beginPath();
  for (let i = 1; i < seats; i++) {
    const x = -w / 2 + 7 + (w - 14) * i / seats;
    c.moveTo(x, -h / 2 + 7); c.lineTo(x, h / 2);
  }
  c.stroke();
}

export const FIXTURES: Record<string, FixtureDef> = {
  toilet: { w: 20, h: 28, label: 'Toilet', draw: (c, w, h) => {
    c.beginPath(); rr(c, -w / 2 + 1, -h / 2, w - 2, 8, 1.5); c.stroke();
    const cy = (-h / 2 + 9 + h / 2 - 1) / 2, ry = (h - 10) / 2 - .5, rx = w / 2 - 2.5;
    c.beginPath(); c.ellipse(0, cy, rx, ry, 0, 0, 7); c.stroke();
    c.beginPath(); c.ellipse(0, cy + 1, rx - 2.6, ry - 2.8, 0, 0, 7); c.stroke(); } },
  sink: { w: 24, h: 21, label: 'Sink', draw: (c, w, h) => {
    c.beginPath(); rr(c, -w / 2, -h / 2, w, h, 1); c.stroke();
    c.beginPath(); rr(c, -w / 2 + 3, -h / 2 + 5, w - 6, h - 8, 3.5); c.stroke();
    c.beginPath(); c.arc(0, -h / 2 + 2.6, 1.3, 0, 7); c.stroke(); } },
  tub: { w: 60, h: 30, label: 'Bathtub', draw: (c, w, h) => {
    c.beginPath(); rr(c, -w / 2, -h / 2, w, h, 2); c.stroke();
    c.beginPath(); rr(c, -w / 2 + 3, -h / 2 + 3, w - 6, h - 6, 7); c.stroke();
    c.beginPath(); c.arc(-w / 2 + 9, 0, 1.8, 0, 7); c.stroke(); } },
  shower: { w: 36, h: 36, label: 'Shower', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    c.beginPath(); rr(c, -w / 2 + 2.5, -h / 2 + 2.5, w - 5, h - 5, 1.5); c.stroke();
    c.beginPath(); c.arc(0, 0, 2, 0, 7); c.stroke();
    c.beginPath(); c.arc(0, -h / 2 + 4, 1.5, 0, 7); c.stroke(); } },
  stove: { w: 30, h: 26, label: 'Range', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    const r = Math.min(w, h) / 6.5;
    for (const sx of [-1, 1]) for (const sy of [-1, 1]) { c.beginPath(); c.arc(sx * w / 4, sy * (h / 4 - .5), r, 0, 7); c.stroke(); } } },
  fridge: { w: 36, h: 30, label: 'Fridge', draw: (c, w, h) => {
    c.beginPath(); rr(c, -w / 2, -h / 2, w, h, 1); c.stroke();
    c.beginPath(); c.moveTo(0, -h / 2); c.lineTo(0, h / 2); c.stroke();
    c.beginPath(); c.moveTo(-3, h / 2 - 4); c.lineTo(-3, h / 2 - 1); c.moveTo(3, h / 2 - 4); c.lineTo(3, h / 2 - 1); c.stroke(); } },
  dishwasher: { w: 24, h: 24, label: 'Dishwasher', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    c.beginPath(); c.moveTo(-w / 2 + 2, -h / 2 + 4.5); c.lineTo(w / 2 - 2, -h / 2 + 4.5); c.stroke();
    c.beginPath(); c.arc(0, 2.5, Math.min(w, h) / 4, 0, 7); c.stroke(); } },
  washer: { w: 27, h: 30, label: 'Washer', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    c.beginPath(); c.moveTo(-w / 2 + 2, -h / 2 + 5); c.lineTo(w / 2 - 2, -h / 2 + 5); c.stroke();
    c.beginPath(); c.arc(0, 2.5, Math.min(w, h) / 3.4, 0, 7); c.stroke();
    c.beginPath(); c.arc(0, 2.5, Math.min(w, h) / 3.4 - 2.5, 0, 7); c.stroke(); } },
  dryer: { w: 27, h: 30, label: 'Dryer', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    c.beginPath(); c.moveTo(-w / 2 + 2, -h / 2 + 5); c.lineTo(w / 2 - 2, -h / 2 + 5); c.stroke();
    c.beginPath(); c.arc(0, 2.5, Math.min(w, h) / 3.4, 0, 7); c.stroke();
    c.beginPath(); c.arc(0, 2.5, 1.4, 0, 7); c.stroke(); } },
  bedk: { w: 76, h: 80, label: 'Bed (king)', draw: drawBed },
  bedq: { w: 60, h: 80, label: 'Bed (queen)', draw: drawBed },
  bedf: { w: 54, h: 75, label: 'Bed (full)', draw: drawBed },
  bedt: { w: 39, h: 75, label: 'Bed (twin)', draw: drawBed },
  crib: { w: 30, h: 54, label: 'Crib', draw: (c, w, h) => {
    c.beginPath(); rr(c, -w / 2, -h / 2, w, h, 2); c.stroke();
    c.beginPath(); rr(c, -w / 2 + 3, -h / 2 + 3, w - 6, h - 6, 1.5); c.stroke();
    c.beginPath();
    for (let y = -h / 2 + 11; y < h / 2 - 8; y += 8) { c.moveTo(-w / 2 + 3, y); c.lineTo(w / 2 - 3, y); }
    c.stroke(); } },
  nightstand: { w: 18, h: 18, label: 'Nightstand', draw: (c, w, h) => {
    c.beginPath(); rr(c, -w / 2, -h / 2, w, h, 1.5); c.stroke();
    c.beginPath(); c.arc(0, 0, Math.min(w, h) / 5, 0, 7); c.stroke(); } },
  dresser: { w: 60, h: 18, label: 'Dresser', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    c.beginPath();
    for (let i = 1; i < 3; i++) { const x = -w / 2 + w * i / 3; c.moveTo(x, -h / 2); c.lineTo(x, h / 2); }
    c.stroke();
    c.beginPath();
    for (let i = 0; i < 3; i++) { const x = -w / 2 + w * (i + .5) / 3; c.moveTo(x - 3, h / 2 - 4); c.lineTo(x + 3, h / 2 - 4); }
    c.stroke(); } },
  wardrobe: { w: 48, h: 24, label: 'Wardrobe', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    c.beginPath(); c.moveTo(0, -h / 2); c.lineTo(0, h / 2); c.stroke();
    c.save(); c.setLineDash([3, 3]);
    c.beginPath(); c.moveTo(-w / 2 + 2, 0); c.lineTo(w / 2 - 2, 0); c.stroke();
    c.restore(); } },
  desk: { w: 48, h: 24, label: 'Desk', draw: (c, w, h) => {
    c.beginPath(); rr(c, -w / 2, -h / 2, w, h, 1.5); c.stroke();
    c.beginPath(); rr(c, -8, h / 2 - 7, 16, 4, 1); c.stroke(); } },
  bookshelf: { w: 36, h: 12, label: 'Bookshelf', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    c.beginPath();
    for (let x = -w / 2 + 6; x < w / 2 - 2; x += 6) { c.moveTo(x, -h / 2); c.lineTo(x, h / 2); }
    c.stroke(); } },
  bench: { w: 48, h: 18, label: 'Bench', draw: (c, w, h) => {
    c.beginPath(); rr(c, -w / 2, -h / 2, w, h, 2); c.stroke();
    c.beginPath(); c.moveTo(-w / 2 + 3, -h / 6); c.lineTo(w / 2 - 3, -h / 6);
    c.moveTo(-w / 2 + 3, h / 6); c.lineTo(w / 2 - 3, h / 6); c.stroke(); } },
  sofa: { w: 84, h: 36, label: 'Sofa', draw: (c, w, h) => drawSofa(c, w, h, 3) },
  loveseat: { w: 60, h: 36, label: 'Loveseat', draw: (c, w, h) => drawSofa(c, w, h, 2) },
  ottoman: { w: 30, h: 30, label: 'Ottoman', draw: (c, w, h) => {
    c.beginPath(); rr(c, -w / 2, -h / 2, w, h, 5); c.stroke();
    c.beginPath(); rr(c, -w / 2 + 3, -h / 2 + 3, w - 6, h - 6, 3.5); c.stroke(); } },
  tvstand: { w: 60, h: 16, label: 'TV stand', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    c.beginPath(); c.lineWidth *= 2.4;
    c.moveTo(-w / 2 + 6, -h / 2 + 3.5); c.lineTo(w / 2 - 6, -h / 2 + 3.5); c.stroke();
    c.lineWidth /= 2.4; } },
  piano: { w: 58, h: 24, label: 'Upright piano', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    c.beginPath(); c.moveTo(-w / 2, h / 2 - 7); c.lineTo(w / 2, h / 2 - 7); c.stroke();
    c.beginPath();
    for (let x = -w / 2 + 4; x < w / 2 - 2; x += 4) { c.moveTo(x, h / 2 - 7); c.lineTo(x, h / 2 - 3.5); }
    c.stroke(); } },
  chair: { w: 18, h: 19, label: 'Chair', draw: (c, w, h) => {
    c.beginPath(); rr(c, -w / 2, -h / 2 + 3, w, h - 3, 2); c.stroke();
    c.beginPath(); rr(c, -w / 2 + 1, -h / 2, w - 2, 3, 1.5); c.stroke(); } },
  table: { w: 48, h: 30, label: 'Table', draw: (c, w, h) => {
    c.beginPath(); rr(c, -w / 2, -h / 2, w, h, 1.5); c.stroke(); } },
  rtable: { w: 42, h: 42, label: 'Round table', draw: (c, w, h) => {
    c.beginPath(); c.ellipse(0, 0, w / 2, h / 2, 0, 0, 7); c.stroke(); } },
  counter: { w: 48, h: 24, label: 'Counter', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    c.beginPath(); c.moveTo(-w / 2, -h / 2 + 1.5); c.lineTo(w / 2, -h / 2 + 1.5); c.stroke(); } },
  stairs: { w: 36, h: 114, label: 'Stairs', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    c.beginPath();
    for (let y = -h / 2 + 10; y < h / 2 - 1; y += 10) { c.moveTo(-w / 2, y); c.lineTo(w / 2, y); }
    c.stroke();
    c.beginPath(); c.moveTo(0, h / 2 - 5); c.lineTo(0, -h / 2 + 7); c.moveTo(-3.5, -h / 2 + 11); c.lineTo(0, -h / 2 + 7); c.lineTo(3.5, -h / 2 + 11); c.stroke();
    c.beginPath(); c.arc(0, h / 2 - 5, 1.6, 0, 7); c.stroke(); } },
  island: { w: 48, h: 36, label: 'Island', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    c.beginPath(); rr(c, -w / 2 + 2, -h / 2 + 2, w - 4, h - 4, 1); c.stroke(); } },
  barstool: { w: 15, h: 15, label: 'Bar stool', draw: (c, w, h) => {
    c.beginPath(); c.ellipse(0, 0, w / 2, h / 2, 0, 0, 7); c.stroke();
    c.beginPath(); c.ellipse(0, 0, w / 2 - 3, h / 2 - 3, 0, 0, 7); c.stroke(); } },
  vanity: { w: 36, h: 21, label: 'Vanity', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    c.beginPath(); rr(c, -9, -h / 2 + 4, 18, h - 7, 3.5); c.stroke();
    c.beginPath(); c.arc(0, -h / 2 + 2.3, 1.2, 0, 7); c.stroke(); } },
  waterheater: { w: 24, h: 24, label: 'Water heater', draw: (c, w, h) => {
    c.beginPath(); c.ellipse(0, 0, w / 2, h / 2, 0, 0, 7); c.stroke();
    c.beginPath(); c.ellipse(0, 0, w / 2 - 2.5, h / 2 - 2.5, 0, 0, 7); c.stroke();
    c.beginPath(); c.arc(0, 0, 1.5, 0, 7); c.stroke(); } },
  fireplace: { w: 54, h: 20, label: 'Fireplace', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    c.beginPath(); c.rect(-w / 2 + 8, -h / 2, w - 16, h - 4); c.stroke();
    c.beginPath(); c.moveTo(-w / 2 + 11, h / 2 - 4); c.quadraticCurveTo(0, -h / 2 + 2, w / 2 - 11, h / 2 - 4); c.stroke(); } },
  plant: { w: 14, h: 14, label: 'Plant', draw: (c, w, h) => {
    c.beginPath(); c.ellipse(0, 0, w / 2, h / 2, 0, 0, 7); c.stroke();
    const r = Math.min(w, h) / 2;
    c.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = i * Math.PI / 3;
      c.moveTo(0, 0); c.quadraticCurveTo(Math.cos(a + .5) * r, Math.sin(a + .5) * r, Math.cos(a) * r * .9, Math.sin(a) * r * .9);
    }
    c.stroke(); } },
  rug: { w: 72, h: 48, label: 'Rug', draw: (c, w, h) => {
    c.beginPath(); rr(c, -w / 2, -h / 2, w, h, 2); c.stroke();
    c.save(); c.setLineDash([4, 3]);
    c.beginPath(); rr(c, -w / 2 + 4, -h / 2 + 4, w - 8, h - 8, 1.5); c.stroke();
    c.restore(); } },
  vanity2: { w: 60, h: 21, label: 'Double vanity', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    for (const sx of [-1, 1]) {
      c.beginPath(); rr(c, sx * w / 4 - 9, -h / 2 + 4, 18, h - 7, 3.5); c.stroke();
      c.beginPath(); c.arc(sx * w / 4, -h / 2 + 2.3, 1.2, 0, 7); c.stroke();
    } } },
  ksink: { w: 33, h: 22, label: 'Kitchen sink', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    for (const sx of [-1, 1]) { c.beginPath(); rr(c, sx * w / 4 - (w / 4 - 3), -h / 2 + 5, w / 2 - 6, h - 8, 2.5); c.stroke(); }
    c.beginPath(); c.arc(0, -h / 2 + 2.6, 1.3, 0, 7); c.stroke(); } },
  pantry: { w: 24, h: 24, label: 'Pantry cabinet', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    c.beginPath(); c.moveTo(0, -h / 2); c.lineTo(0, h / 2); c.stroke();
    c.save(); c.setLineDash([3, 3]);
    c.beginPath(); c.moveTo(-w / 2 + 2, 0); c.lineTo(w / 2 - 2, 0); c.stroke();
    c.restore(); } },
  furnace: { w: 30, h: 30, label: 'Furnace', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    c.beginPath(); rr(c, -w / 2 + 3, -h / 2 + 3, w - 6, h - 6, 1.5); c.stroke();
    c.beginPath(); c.arc(0, 0, Math.min(w, h) / 5, 0, 7); c.stroke();
    c.beginPath(); c.moveTo(0, -Math.min(w, h) / 5); c.lineTo(0, Math.min(w, h) / 5); c.stroke(); } },
  closetrod: { w: 48, h: 24, label: 'Closet rod', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    // rod (solid) with dashed shelf line at the back — standard closet symbol
    c.beginPath(); c.moveTo(-w / 2 + 2, 0); c.lineTo(w / 2 - 2, 0); c.stroke();
    c.beginPath(); c.arc(0, 0, 1.6, 0, 7); c.stroke();
    c.save(); c.setLineDash([4, 3]);
    c.beginPath(); c.moveTo(-w / 2 + 2, -h / 2 + 5); c.lineTo(w / 2 - 2, -h / 2 + 5); c.stroke();
    c.restore(); } },
  shelves: { w: 36, h: 16, label: 'Shelving', draw: (c, w, h) => {
    c.beginPath(); c.rect(-w / 2, -h / 2, w, h); c.stroke();
    c.save(); c.setLineDash([4, 3]);
    for (const fy of [-1, 1]) { c.beginPath(); c.moveTo(-w / 2 + 2, fy * h / 6); c.lineTo(w / 2 - 2, fy * h / 6); c.stroke(); }
    c.restore(); } },
  armchair: { w: 30, h: 32, label: 'Armchair', draw: (c, w, h) => drawSofa(c, w, h, 1) },
  sectional: { w: 96, h: 72, label: 'Sectional', draw: (c, w, h) => {
    const d = Math.min(36, w / 2, h / 2);   // seat depth; chaise leg on the left
    c.beginPath();
    c.moveTo(-w / 2, -h / 2); c.lineTo(w / 2, -h / 2); c.lineTo(w / 2, -h / 2 + d);
    c.lineTo(-w / 2 + d, -h / 2 + d); c.lineTo(-w / 2 + d, h / 2); c.lineTo(-w / 2, h / 2);
    c.closePath(); c.stroke();
    c.beginPath(); rr(c, -w / 2 + 6, -h / 2 + 6, w - 12, d - 9, 1.5); c.stroke();
    c.beginPath(); rr(c, -w / 2 + 6, -h / 2 + d, d - 9, h - d - 6, 1.5); c.stroke(); } },
  ctable: { w: 48, h: 24, label: 'Coffee table', draw: (c, w, h) => {
    c.beginPath(); rr(c, -w / 2, -h / 2, w, h, 2.5); c.stroke();
    c.beginPath(); rr(c, -w / 2 + 3, -h / 2 + 3, w - 6, h - 6, 1.5); c.stroke(); } },
  car: { w: 72, h: 190, label: 'Car', draw: (c, w, h) => {
    c.beginPath(); rr(c, -w / 2, -h / 2, w, h, 12); c.stroke();
    // hood at the back edge (-y), cabin behind it
    c.beginPath(); rr(c, -w / 2 + 5, -h / 2 + h * .28, w - 10, h * .42, 6); c.stroke();
    c.beginPath(); c.moveTo(-w / 2 + 7, -h / 2 + h * .28 + 8); c.lineTo(0, -h / 2 + h * .22);
    c.lineTo(w / 2 - 7, -h / 2 + h * .28 + 8); c.stroke(); } },
  box: { w: 24, h: 24, label: 'Box', draw: (c, w, h) => {
    c.beginPath(); rr(c, -w / 2, -h / 2, w, h, 1); c.stroke(); } },
};

/** Palette organization: one section per room/category, in placement-priority
    order. This is the single source of truth for catalog order — the panel,
    docs, and showcase layout all follow it. */
export const FIXTURE_CATEGORIES: { name: string; items: string[] }[] = [
  { name: 'Bath', items: ['toilet', 'sink', 'vanity', 'vanity2', 'tub', 'shower'] },
  { name: 'Kitchen', items: ['ksink', 'stove', 'fridge', 'dishwasher', 'counter', 'island', 'pantry', 'barstool'] },
  { name: 'Laundry & utility', items: ['washer', 'dryer', 'waterheater', 'furnace'] },
  { name: 'Bedroom', items: ['bedk', 'bedq', 'bedf', 'bedt', 'crib', 'nightstand', 'dresser', 'wardrobe'] },
  { name: 'Closet & storage', items: ['closetrod', 'shelves', 'bookshelf'] },
  { name: 'Living', items: ['sofa', 'loveseat', 'sectional', 'armchair', 'chair', 'ottoman', 'bench', 'ctable', 'tvstand', 'fireplace', 'piano', 'rug', 'plant'] },
  { name: 'Dining & office', items: ['table', 'rtable', 'desk'] },
  { name: 'Structure & misc', items: ['stairs', 'car', 'box'] },
];

export const FIXTURE_ORDER = FIXTURE_CATEGORIES.flatMap(c => c.items);

/** Search synonyms for the palette search bar (matched with label + category). */
export const FIXTURE_KW: Record<string, string> = {
  toilet: 'wc commode bathroom', sink: 'basin lavatory bathroom', vanity: 'bathroom cabinet',
  vanity2: 'bathroom double sink his hers', tub: 'bath bathtub soaking', shower: 'stall bathroom',
  ksink: 'kitchen double bowl basin', stove: 'range oven cooktop', fridge: 'refrigerator freezer',
  dishwasher: 'dw', counter: 'cabinet base countertop run', island: 'kitchen peninsula',
  pantry: 'tall cabinet larder storage', barstool: 'stool seat',
  washer: 'laundry washing machine', dryer: 'laundry', waterheater: 'tank boiler wh',
  furnace: 'hvac heater air handler mechanical',
  bedk: 'king', bedq: 'queen', bedf: 'full double bed', bedt: 'twin single', crib: 'baby nursery',
  nightstand: 'bedside side table', dresser: 'chest drawers bureau', wardrobe: 'armoire closet',
  closetrod: 'hanging rod rail clothes closet', shelves: 'shelf storage unit', bookshelf: 'bookcase shelf',
  sofa: 'couch', loveseat: 'couch small sofa', sectional: 'couch l-shaped chaise corner',
  armchair: 'lounge chair accent recliner', chair: 'dining seat', ottoman: 'footstool pouf',
  bench: 'seat entry mudroom', ctable: 'coffee cocktail low table', tvstand: 'media console television entertainment',
  fireplace: 'hearth mantel', piano: 'upright music', rug: 'carpet area', plant: 'tree pot greenery',
  table: 'dining kitchen', rtable: 'round dining pedestal', desk: 'office work',
  stairs: 'staircase steps', car: 'garage vehicle parking driveway', box: 'generic custom placeholder',
};
